import { t } from 'i18next'

import { memo, useMemo } from 'react'
import { cartChangeQuotableSeatQuantity } from '@/store/cartSlice/thunks/cartChangeQuotableSeatQuantity.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { warehouseSelectedElementAvailabilitySelector } from '@/store/selectors/warehouseSelectedElementAvailabilitySelector'
import { ItemQuantityController } from '../../../components/ItemQuantityController/ItemQuantityController'
import { useAppDispatch, useAppSelector } from '../../../hooks/store'
import { ChangeQuantityActionType } from '../../../models/cart'
import { MapSetupItem, MapSetupItemKeyName } from '../../../models/map'
import { toastSlice } from '../../../store/toastSlice'

interface Props {
  remainingGlobal: number
  item: MapSetupItem
}

export const SetupSeatListItem = memo(
  function SetupSeatListItem(props: Props) {
    const dispatch = useAppDispatch()

    const warehouseAvailability = useAppSelector(
      warehouseSelectedElementAvailabilitySelector
    )
    const license = useAppSelector(licenseSlice.selectors.license)

    const warehouseAvailable = useMemo(() => {
      if (!license?.warehouseOnline) {
        return Infinity
      }

      switch (props.item.keyName) {
        case MapSetupItemKeyName.BED: {
          return warehouseAvailability.beds
        }
        case MapSetupItemKeyName.CHAIR: {
          return warehouseAvailability.chairs
        }
        case MapSetupItemKeyName.DECK_CHAIR: {
          return warehouseAvailability.deckChairs
        }
        case MapSetupItemKeyName.MAXI_BED: {
          return warehouseAvailability.maxiBeds
        }
        default: {
          return Infinity
        }
      }
    }, [warehouseAvailability, props.item.keyName])

    const dynamicKey = props.item.keyName

    const handleAddSeat = () => {
      dispatch(
        cartChangeQuotableSeatQuantity({
          actionType: ChangeQuantityActionType.ADD,
          dynamicKey,
        })
      )
    }

    const handleRemoveSeat = () => {
      dispatch(
        cartChangeQuotableSeatQuantity({
          actionType: ChangeQuantityActionType.REMOVE,
          dynamicKey,
        })
      )
    }

    const onAddDisabled = () =>
      dispatch(
        toastSlice.actions.show({
          message: t('map.elementDetail.maximumSeatsLimitReached'),
        })
      )

    return (
      <ItemQuantityController
        available={{
          global: Math.min(props.remainingGlobal, warehouseAvailable),
          group: props.item?.max,
          ticket: props.item.max,
        }}
        hasLimit={props.item.max >= 0}
        icon={props.item.icon}
        initialQuantity={props.item.d}
        key={props.item.keyName}
        minimumQuantity={props.item?.min}
        name={props.item.label}
        onAdd={handleAddSeat}
        onAddDisabled={onAddDisabled}
        onRemove={handleRemoveSeat}
      />
    )
  },
  (prevProps: Props, nextProps: Props) =>
    prevProps.remainingGlobal === nextProps.remainingGlobal &&
    prevProps.item.keyName === nextProps.item.keyName
)
